/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React from 'react';
import { canonicalizeExpressionPaths } from './mapping-value-round-trip';
import { Box, Button, chakra, Checkbox, HStack, Input, NativeSelect, Text, Tooltip, VStack } from '@chakra-ui/react';
import {
  type MacroExpression,
  type ComputedExpression,
  type ComputedOperation,
  type ConditionalExpression,
  type MacroCondition,
  type ValueOperand,
  type ComputedOperand,
  type CollectionOperand,
  operationArity,
  readComputedOperands,
  readConditionOperands,
  conditionRhsShape,
  CONDITION_OPERATORS,
  type ConditionOperator,
  defaultMacroCondition,
  defaultStaticExpression,
  defaultValueOperand,
  openingOperands,
  type CriteriaNode,
} from '../../../types/decorator-expression';
import {
  flattenCaseLadder,
  ladderFromRungs,
  MAX_CONDITIONAL_NESTING,
  moveRung,
  type CaseLadder,
  type CaseRung,
} from './case-ladder.model';
import { comparesText, flattenPredicate, predicateFromList, type ConditionListMode } from './condition-builder.model';
import {
  boundReads,
  proposalsIn,
  scopeAt,
  stampReadBindings,
  type LadderPosition,
  type TestedScope,
} from './tested-value.model';
import { TestedScopeContext, useTestedScope } from './tested-scope-context';
import { EmptyValueContext, emptyValueSentence, type EmptyValueOnSurface } from './empty-value-context';
import { ListOrPathField } from '../inputs/ListOrPathField';
import { type MappingSuggestion } from '../inputs/MappingAutocompleteInput';
import { NumberField } from '../inputs/NumberField';
import { eachMemberSuggestions, getSchemaSuggestions } from '../inputs/data-model-schema';
import { openSlotForShape } from './CapabilityChipRow';
import { ExpressionLibraryAccessContext } from './expression-library-access';
import { useSharedExpressionSlot } from './useSharedExpressionSlot';
import { SharedExpressionSurfaces } from './SharedExpressionSurfaces';
import type { CapabilityRefused } from './MappingControl';
import type { ResolveSurfaces } from './shared-expression-usability';
import { referenceReplacing, reshapeExpression, unsuppressed } from './value-line';
import { ExpressionValueLine } from './ExpressionValueLine';
import { RowNestedFindingsContext } from './row-nested-findings';
import { IssueDot } from './ValidationSummary';
import { ISSUE_INK } from './mapping-issues';
import { findingLabel } from '@finding-kind';
import { operandFromLine, operandOnLine } from './collection-operand';
import { KnobLabel } from './mapping-knobs';
import { MAPPING_HELP } from './mapping-help';
import { OMISSION_ANSWER_LABEL } from './mapping-value-round-trip';
import { FieldHelpTooltip } from '../../ui/FieldHelpTooltip';
import { ControlHover } from '../../ui/ControlHover';
import { useExpressionLibrary } from '../../../hooks/useExpressionLibrary';
import { derivedYield, EXPRESSION_YIELD_LABEL, EXPRESSION_YIELD_TYPES, isExpressionYieldType } from '@expression-yield';
import {
  EXPRESSION_LOCATION_ATTRIBUTE,
  encodeLocation,
  type ExpressionLocation,
  type ExpressionLocationStep,
} from './expression-walk';
export { summarizeMacroExpression } from './expression-summary';

/**
 * Field-path autocomplete source for every path input in this editor and its
 * nested forms. Defaults to the outbound bidBrain context schema
 * ({@link getSchemaSuggestions}); an **inbound** consumer (response mapping) can
 * override it via {@link DecoratorExpressionEditorProps.getFieldSuggestions} so
 * expression paths autocomplete against the `response.*` catalog rather than the
 * session/request library. Provided as context so the deeply-nested operand /
 * conditional sub-forms pick it up without prop-drilling through each one.
 */
export type FieldSuggestionsFn = (query: string) => MappingSuggestion[] | Promise<MappingSuggestion[]>;
const FieldSuggestionsContext = React.createContext<FieldSuggestionsFn>(getSchemaSuggestions);
const useFieldSuggestions = (): FieldSuggestionsFn => React.useContext(FieldSuggestionsContext);

/**
 * **Where in the value this part of the editor is drawing** (`4c`, `#1334`).
 *
 * A finding inside an expression carries a location in the walk's terms — *Case 2 › condition 1 ›
 * Value to test* — and opening its row has to land there, not at the top of a four-case ladder. So
 * each place the walk names stamps its location on what it draws, and the reveal finds it inside the
 * row. Context rather than a prop, for the reason `FieldSuggestionsContext` gives: the forms nest
 * through each other and a prop threaded through all of them is a prop one of them drops.
 *
 * **`null` means *not this value's locations*** — the surfaces under a `Shared…` chip draw another
 * expression, whose findings are its author's (`#1328`). Stamping them would put a second
 * `case0` inside the row for a reveal to find first.
 */
const ExpressionLocationContext = React.createContext<ExpressionLocation | null>([]);

/** One step of the location, stamped on a wrapper around what is drawn there. */
const AtLocation: React.FC<{ step: ExpressionLocationStep; children: React.ReactNode }> = ({ step, children }) => {
  const parent = React.useContext(ExpressionLocationContext);
  const findings = React.useContext(RowNestedFindingsContext);
  if (parent === null) return <>{children}</>;
  const at = [...parent, step];
  const token = encodeLocation(at);
  /**
   * **The place a finding names carries the mark while the row is open** (`3i`, Todd 2026-09-22) —
   * the row's dot and pill said it while the row was shut. Drawn only where there is one, so a clean
   * expression lays out exactly as before, and with the same two facts the row gives: the sentence
   * as the dot's name, and the kind in two words.
   */
  const here = findings?.get(token) ?? null;
  const body = <ExpressionLocationContext.Provider value={at}>{children}</ExpressionLocationContext.Provider>;
  return (
    <Box {...{ [EXPRESSION_LOCATION_ATTRIBUTE]: token }}>
      {here ? (
        <HStack align="flex-start" gap={2} data-nested-finding={here.level}>
          <HStack gap={1} flexShrink={0} pt="7px">
            <IssueDot level={here.level} title={here.detail} />
            <Text fontSize="2xs" fontWeight="600" color={ISSUE_INK[here.level]} whiteSpace="nowrap" aria-hidden="true">
              {findingLabel(here.kind)}
            </Text>
          </HStack>
          <Box flex="1" minW={0}>
            {body}
          </Box>
        </HStack>
      ) : (
        body
      )}
    </Box>
  );
};

/** Draws another expression than the value being located — see {@link ExpressionLocationContext}. */
export const OutsideExpressionLocations: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ExpressionLocationContext.Provider value={null}>{children}</ExpressionLocationContext.Provider>
);

// ---------------------------------------------------------------------------
// Collections for Chakra Select components
// ---------------------------------------------------------------------------

/**
 * **The operation vocabulary, and how each one reads between its operands.**
 *
 * `reads` is the symbol rendered *between* operand rows — Todd's *"value, operand, value"* model
 * (`#1259`), which `E2b` already applies to a condition and which lands here for the same reason:
 * `X43`'s rule is that operands apply **left to right with no precedence**, and a rule the control
 * does not show is one the row implies. A producer has nothing between anything, so it reads `null`.
 *
 * The two groups are {@link operationArity} made visible: an operation either combines the operands
 * or produces a value from none. There is no third answer and no operand kind that produces one —
 * that was the `fn` operand, retired in `E3`.
 */
const OPERATIONS: Record<ComputedOperation, { label: string; reads: string | null }> = {
  concat: { label: 'concat (join as strings)', reads: 'then' },
  add: { label: 'add (sum)', reads: '+' },
  subtract: { label: 'subtract', reads: '−' },
  multiply: { label: 'multiply', reads: '×' },
  divide: { label: 'divide', reads: '÷' },
  /**
   * **The one operation a collection brought** (`CO8`, `2e`) — every other already folds over a list's
   * members, so `add` over `[*]` is a sum under its own name (Todd: one concept at two arities, one
   * label). Between its operands it reads *and*: an average has no symbol that sits between two terms.
   */
  avg: { label: 'avg (average)', reads: 'and' },
  timestamp: { label: 'timestamp – current Unix ms', reads: null },
  rnd: { label: 'rnd – random integer', reads: null },
  uuid: { label: 'uuid – random UUID string', reads: null },
};

/**
 * Keyed by the closed vocabulary, so an operation cannot be added without a label — this was a
 * hand-kept array, which is a list beside the members that nothing checks.
 */
const OPERATION_OPTIONS: { label: string; value: ComputedOperation; reads: string | null }[] = (
  Object.entries(OPERATIONS) as [ComputedOperation, { label: string; reads: string | null }][]
).map(([value, option]) => ({ value, ...option }));

/** How an operation reads between two operands. `null` for a producer, which has no operands. */
const operationReads = (operation: ComputedOperation): string | null =>
  OPERATION_OPTIONS.find((o) => o.value === operation)?.reads ?? null;

/** What this operation is called, for the group's accessible name. The picker's own words. */
const operationLabel = (operation: ComputedOperation): string =>
  OPERATION_OPTIONS.find((o) => o.value === operation)?.label ?? operation;

// ---------------------------------------------------------------------------
// Sub-editors for each expression type
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Computed expression editor
// ---------------------------------------------------------------------------

interface ComputedEditorProps {
  expr: ComputedExpression;
  onChange: (expr: ComputedExpression) => void;
}

/**
 * **A calculation is an operation over a list of value lines** — `X43` as re-ratified, `E3`
 * (`#1271`).
 *
 * Two things changed here and they are one change. The operand row **was** the last type dropdown in
 * this editor (`Type: literal / path / fn`) — `X39` one level below where it was written, and the
 * reason *"a group"* could not simply be added: it would have been a fourth entry in a picker `X39`
 * says should not exist. So an operand is now an {@link ExpressionValueLine} like every other value
 * in this control, and `group` is one more shape that line holds.
 *
 * The built-ins went the other way, to the **operation** (Todd, 2026-09-08). That is where `rnd`'s
 * bounds have a home; as a suggestion in a path type-down they had none, which the first answer had
 * already recorded as unresolved.
 */
const ComputedExpressionForm: React.FC<ComputedEditorProps> = ({ expr, onChange }) => {
  const operation = expr.operation ?? 'concat';
  const operands = readComputedOperands(expr);
  const produces = operationArity(operation) === 'produces';

  /**
   * **Choosing an operation opens it to the operands it reads** (`X53`) — the rule is
   * {@link openingOperands} and this is one of its two call sites, the other being the `Calculate…`
   * chip. Padding only ever **adds** an empty row, so it is an edit and not a reset (`#1223`): a
   * three-operand calculation switched between combining operations is untouched, and a producer's
   * operands are kept exactly as the sentence below promises.
   */
  const setOperation = (next: ComputedOperation) =>
    onChange({ ...expr, operation: next, operands: openingOperands(next, operands) });

  const updateOperand = (index: number, operand: ComputedOperand) => {
    const next = [...operands];
    next[index] = operand;
    onChange({ ...expr, operands: next });
  };

  const removeOperand = (index: number) => {
    const next = operands.filter((_, i) => i !== index);
    onChange({ ...expr, operands: next.length > 0 ? next : [defaultValueOperand()] });
  };

  const addOperand = () => onChange({ ...expr, operands: [...operands, defaultValueOperand()] });

  return (
    /**
     * **The group is a box, and its operation is on the header** (`X53`, `#1284`) — Todd, on `E3`'s
     * rehearsal: *"the groups have no visual distinction, and the operation selection at the top of
     * the group makes it tough to see what's going to happen. Creating the grouping in a box (like
     * the comparison stack does) may make that easier to understand."*
     *
     * The idiom is the case rung's, deliberately, because an operator has already learned it here —
     * *one name per concept* applied to a layout, so a second grouping idiom is not invented for the
     * thing the ladder already draws. What it does **not** borrow is the rung's `warning` family: a
     * calculation is not a warning, and a semantic colour would be a claim about severity where the
     * only claim is *these operands belong together*. So it is the neutral ground, which is also
     * what makes a group nested in a group legible — each border is one bracket.
     *
     * It is on the **form**, not on the caller, so every depth gets it from one place: the value's
     * own calculation, an operand that is a group, and a branch's. A box added per call site is the
     * per-caller special case that produces the next disagreement.
     *
     * **And the grouping is in the accessible tree, not only in the border.** A box is a claim that
     * these controls belong together; drawn in CSS alone it is a claim only a sighted operator can
     * read, which is `#1276`'s rule met before it is broken rather than after. The name carries the
     * operation because that is what distinguishes one group from the group it is nested in.
     */
    <Box
      role="group"
      aria-label={`Calculation: ${operationLabel(operation)}`}
      bg="bg.subtle"
      borderRadius="6px"
      p={2}
      border="1px solid"
      borderColor="border.default"
    >
      <VStack gap={2} align="stretch">
        {/* **The header: what this box does, what that needs, and the way to add to it.** The
            Default is the value line's, one level up — a `computed` declares one and
            `ExpressionDefaultControl` renders it there, so a box here is the second control over one
            slot that `X38` exists to remove. */}
        <HStack gap={2} align="flex-end" wrap="wrap">
          <Box minW="220px" flex="0 0 auto">
            <Box mb={1}>
              <KnobLabel label="Operation" help={MAPPING_HELP.operation} />
            </Box>
            {/* `NativeSelect` for `#1242`'s measurement — it renders plain text — so it needs no
                `Portal` wrapper to avoid `#1240`'s push-the-layout defect. The two groups are
                `operationArity`: combine the operands, or produce a value from none. */}
            <NativeSelect.Root size="sm">
              <NativeSelect.Field
                aria-label="Operation"
                value={operation}
                onChange={(e) => setOperation(e.currentTarget.value as ComputedOperation)}
              >
                <optgroup label="Combine the operands">
                  {OPERATION_OPTIONS.filter((o) => operationArity(o.value) === 'combines').map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Produce a value">
                  {OPERATION_OPTIONS.filter((o) => operationArity(o.value) === 'produces').map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </optgroup>
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Box>
          {operation === 'concat' && (
            <Box w="120px" flex="0 0 auto">
              <Text fontSize="xs" color="fg.muted" mb={1}>
                Separator
              </Text>
              <Input
                size="sm"
                bg="bg.card"
                value={expr.separator ?? ''}
                placeholder="e.g. _"
                onChange={(e) => onChange({ ...expr, separator: e.target.value || undefined })}
                borderColor="border.default"
                _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)' }}
              />
            </Box>
          )}
          {/* `rnd`'s bounds, on the thing that declares the operation — which is the whole argument
              for moving the built-ins here. Through `NumberField`, because `Number('')` is `0` and a
              box that refuses to empty is the defect that keeps coming back one field at a time. */}
          {operation === 'rnd' && (
            <>
              <Box w="100px" flex="0 0 auto">
                <Text fontSize="xs" color="fg.muted" mb={1}>
                  Min
                </Text>
                <NumberField
                  minDecimals={null}
                  size="sm"
                  bg="bg.card"
                  aria-label="Minimum for rnd"
                  placeholder="0"
                  value={expr.min}
                  onChange={(min) => onChange({ ...expr, min })}
                  borderColor="border.default"
                />
              </Box>
              <Box w="120px" flex="0 0 auto">
                <Text fontSize="xs" color="fg.muted" mb={1}>
                  Max
                </Text>
                <NumberField
                  minDecimals={null}
                  size="sm"
                  bg="bg.card"
                  aria-label="Maximum for rnd"
                  placeholder="999999999"
                  value={expr.max}
                  onChange={(max) => onChange({ ...expr, max })}
                  borderColor="border.default"
                />
              </Box>
            </>
          )}
          {/**
           * **The add sits on the header because the box is the operand list** — there is no longer
           * an `Operands` sub-heading for it to belong to, and the reading rule it carried is not
           * dropped with the row: `MAPPING_HELP.operands` travels onto this label, which is where an
           * operator is standing when they wonder what order these apply in.
           *
           * Adds **inline**, with no separate open or cancel — which is why `#1262`'s
           * add-and-open defect does not exist on this control, and a property to keep.
           */}
          {!produces && (
            <>
              <Box flex="1" />
              <HStack gap={2} align="center" flex="0 0 auto">
                <KnobLabel label="Operands" help={MAPPING_HELP.operands} />
                <Button size="xs" bg="accent.solid" color="white" _hover={{ bg: 'accent.ink' }} onClick={addOperand}>
                  + Add Operand
                </Button>
              </HStack>
            </>
          )}
        </HStack>

        {produces ? (
          /**
           * **A producer takes no operands, and the ones it was given are kept** (`#1223`: a mode
           * switch is an edit, not a reset).
           *
           * So the list is not rendered and the operator is told **both** halves — that it is not
           * used *and* that it is still there. Saying only the first would read as a silent discard,
           * which is the loss the carry rule exists to prevent; saying neither would be the same
           * loss with nothing on screen at all.
           */
          <Text fontSize="xs" color="fg.muted">
            This produces a value on its own, so it takes no operands.
            {operands.length > 0
              ? ` The ${operands.length === 1 ? 'operand' : `${operands.length} operands`} you authored ${
                  operands.length === 1 ? 'is' : 'are'
                } kept — switch back to an operation that combines them and ${
                  operands.length === 1 ? 'it returns' : 'they return'
                }.`
              : ''}
          </Text>
        ) : (
          <VStack gap={1} align="stretch">
            {operands.map((operand, idx) => (
              <React.Fragment key={idx}>
                {/**
                 * **The operation reads between its operands**, and it is a read-out rather than a
                 * second control: the operation is chosen once, on the header. What it is doing here
                 * is making *left to right, no precedence* **visible** — `X43`'s own requirement, and
                 * the thing `x - y * z` silently got wrong before the group existed.
                 *
                 * It appears **only once there are rows for it to sit between** (Todd: *"showing the
                 * operator between rows helps — but only after the user adds operands"*), which is
                 * what `idx > 0` says: a one-operand calculation has no order to make visible, and a
                 * symbol with nothing on the other side of it would be describing something that is
                 * not there.
                 */}
                {idx > 0 && operationReads(operation) && (
                  <Text fontSize="xs" color="fg.muted" fontFamily="mono" pl={1}>
                    {operationReads(operation)}
                  </Text>
                )}
                <AtLocation step={{ kind: 'operand', index: idx }}>
                  <ValueOperandLine
                    label={null}
                    operand={operand}
                    onChange={(next) => updateOperand(idx, next)}
                    onCollection={(next) => updateOperand(idx, next)}
                    remove={
                      operands.length > 1 ? { label: `Remove operand ${idx + 1}`, act: () => removeOperand(idx) } : null
                    }
                  />
                </AtLocation>
              </React.Fragment>
            ))}
          </VStack>
        )}
      </VStack>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Conditional expression editor
// ---------------------------------------------------------------------------

interface BranchEditorProps {
  label: string;
  /**
   * **`undefined` is a real state and only the ladder's `Otherwise` has it** (`#1251`).
   *
   * A stored conditional with no `else` resolves to `{ resolved: false }`, which on an ORTB body
   * leaf is the only spelling of *accept the system default*. The invariant is that `undefined`
   * implies {@link BranchEditorProps.onUnanswer} is non-null.
   */
  expr: MacroExpression | undefined;
  onChange: (expr: MacroExpression) => void;
  /**
   * **How many conditionals enclose this branch**, 1 for the value's own (`E4`, `#1272`).
   *
   * It decides one thing and states it twice: whether `If…` is offered here, and whether a
   * conditional already sitting here is rendered or reported as too deep. One predicate — `depth <
   * MAX_CONDITIONAL_NESTING` — so the editor cannot offer a nesting it would then refuse to draw.
   */
  depth: number;
  /**
   * Return this branch to having **no answer at all** — required and nullable, `null` on a `then`.
   *
   * As `?:` this rendered nothing on whichever call site forgot it, and the third option is gated on
   * its truthiness below, so *forgot* and *declined* would be the same code (`#1134`). A `then` is
   * `null` because a rung with no outcome is not a rung; the `Otherwise` passes a handler because
   * *no answer* is the state it can legitimately be in.
   */
  onUnanswer: (() => void) | null;
}

/**
 * **The one thing a branch still cannot hold, and where to put it instead** (`E4`, `#1272`).
 *
 * `BRANCH_TAKES_A_VALUE` stood here and named **two** refusals — the recursion and the shared
 * expression — and it deliberately made no redirect, because the only one available pointed at the
 * `Shared…` chip which the same branch refused. *A refusal that sends you somewhere that also
 * refuses you* is what the **follow your own redirect** rule is about, and writing the sentence
 * without one was the honest half-measure while both were true.
 *
 * `E4` removes both halves of that. `shared` is offered on a branch (Todd, 2026-09-10 — `E4` is the
 * request the 2026-09-08 deferral was waiting for), so the redirect now works; and the recursion is
 * **bounded** rather than refused, so this fires only above the bound instead of always.
 *
 * Three things the sentence has to do, and each is a rule this repo already pays for:
 *
 * - **Say the operator's outcome, not our mechanism** (`#1045`). *Levels* is what they can count on
 *   screen; *recursion depth* is ours.
 * - **Redirect to something that works, and check it** — the `Shared…` chip it names is rendered by
 *   this same component, one chip along, and it opens the library.
 * - **Refuse, never remove** (pattern 24). The chip is present and disabled with this beside it, so
 *   the operator learns the bound rather than failing to find the control.
 */
const BRANCH_NESTING_BOUND: CapabilityRefused = {
  reason:
    `A decision inside a decision is as deep as this editor reads — ${MAX_CONDITIONAL_NESTING} levels. ` +
    'Author the deeper decision as a shared expression, then address it here with Shared….',
};

/**
 * **A conditional already sitting deeper than the bound** — a document authored before it, through the
 * raw editor, or by a shared expression that was substituted in.
 *
 * `libs/dto` allows any depth and the engine resolves any depth, so this is a view saying what it
 * cannot show rather than a defect: *state what is in force, or say you do not know*. It touches
 * nothing, which is what makes it safe to render over an expression we are not equipped to edit.
 */
const BRANCH_TOO_DEEP_TO_SHOW =
  'This outcome is itself a decision, nested deeper than this editor reads. It is left exactly as it is — ' +
  'edit the stored expression directly, or lift the inner decision into a shared expression and address it here.';

/**
 * **A branch answers the same two questions the row does: what value, and is it on the wire**
 * (`X38`, and `X41`/`X7a` for where `suppress` lives).
 *
 * It used to answer a third — a **Type** dropdown offering *Static value · Dynamic path · Suppress*
 * — which is the control `X39` retired one level up, surviving one level down. Its first two arms
 * are what the value line derives from what was typed (`X24`), so on a branch they were a second
 * control for a decision already being made beside them: `F5`'s shape, in the editor built to end
 * it. Todd, on this build: the two-option wire answer *"is X39 applied one level down"*.
 *
 * **The removal ships with its replacement, which is the condition** (the mode-switch rule). The
 * dropdown was the only way to make a branch suppress, so retiring it alone would have made *omit
 * the field* unauthorable — `#1223` for the fourth time, wearing a tidy-up. So the third arm becomes
 * the row's own omission question, in the row's own words: `X46a` heads that control by its subject,
 * and `OMISSION_ANSWER_LABEL` supplies *Never send it* rather than a second spelling of it.
 */
const BRANCH_WIRE_ANSWERS = ['send', 'never'] as const;
type BranchWireAnswer = (typeof BRANCH_WIRE_ANSWERS)[number];

const BRANCH_WIRE_LABEL: Record<BranchWireAnswer, string> = {
  // Not `OMISSION_ANSWER_LABEL.always`: on a branch the condition is the ladder's, so *always* would
  // be a claim about every request that this outcome does not make. What it says is *this branch
  // sends a value*, and the value is directly below it.
  send: 'Send this value',
  never: OMISSION_ANSWER_LABEL.never,
};

/**
 * *This branch has no answer at all* — the ladder's `Otherwise`, and nothing else (`#1251`).
 *
 * It is a **wire answer** rather than a value, which is why it belongs in this select: the three
 * options are the three things that can reach the exchange from this branch — a value, nothing, or
 * whatever was already there.
 */
const BRANCH_UNANSWERED = 'unanswered' as const;

const BranchWireSelect: React.FC<{
  expr: MacroExpression | undefined;
  onChange: (expr: MacroExpression) => void;
  onUnanswer: (() => void) | null;
}> = ({ expr, onChange, onUnanswer }) => {
  const current = expr === undefined ? BRANCH_UNANSWERED : expr.type === 'suppress' ? 'never' : 'send';
  return (
    <HStack gap={2} align="center">
      <KnobLabel label="Is this on the wire" help={MAPPING_HELP.omission} />
      <NativeSelect.Root size="sm" width="fit-content">
        <NativeSelect.Field
          aria-label="Is this branch on the wire"
          value={current}
          onChange={(e) => {
            const next = e.currentTarget.value;
            if (next === current) return;
            if (next === BRANCH_UNANSWERED) {
              onUnanswer?.();
              return;
            }
            // `reshapeExpression` is what carries the note across — *a note is about the mapping, not
            // about the shape it currently has* — so switching to *never send it* and back does not
            // silently drop the sentence explaining why.
            //
            // **And the way back is `unsuppressed`, not `reshapeExpression(…, 'static')`** (`#1274`).
            // The suppression is the only thing that knows what shape it took away; asking for a
            // literal here hands a path back as an empty box, which is the defect this closes.
            if (next === 'never') {
              onChange(reshapeExpression(expr ?? defaultStaticExpression(), 'suppress'));
              return;
            }
            onChange(unsuppressed(expr));
          }}
        >
          {BRANCH_WIRE_ANSWERS.map((answer) => (
            <option key={answer} value={answer}>
              {BRANCH_WIRE_LABEL[answer]}
            </option>
          ))}
          {/* Offered only where it is reachable, which is the `Otherwise` (`#1134`: the capability is
              declared, so a surface that does not have it cannot silently omit the option). */}
          {onUnanswer && <option value={BRANCH_UNANSWERED}>Leave it to whatever is already there</option>}
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>
    </HStack>
  );
};

/**
 * **A branch outcome is a value, so it is the same value line** — `X42`, `X38`.
 *
 * *A case outcome is a full value line* was ratified from canvas `2d`; until this change it was a
 * `CalculatedExpressionForm` with a chip row bolted under it, which is why a branch showed its
 * transform twice and offered both an extract-with-regex checkbox and an Extract chip. One control
 * now, with the chips owning Extract and Transform at every depth.
 *
 * **What it still declines is the recursion, and it says so** (pattern 24). A branch cannot hold a
 * `conditional` or a `computed` — the console narrows `then`/`else` to `Static | Calculated |
 * Suppress`, which is **#542**, and `X42` is that the narrowing is a *rendering* requirement rather
 * than a type widening. The chips are present and unavailable rather than absent, and the day #542
 * widens them this component changes in one place.
 */
const BranchExpressionForm: React.FC<BranchEditorProps> = ({ label, expr, onChange, onUnanswer, depth }) => {
  const getFieldSuggestions = useFieldSuggestions();
  /* **The `Shared…` slot is a property of the value, not of the branch** (`X30c`, `#1303`). This
     component held its own copy of the older *open the picker* behaviour, which is why a shared
     expression inside a conditional could be picked and then never opened, edited or replaced. */
  const shared = useSharedExpressionSlot(expr);
  const emptySentence = emptyValueSentence(React.useContext(EmptyValueContext));

  /**
   * **One predicate for the offer and for the drawing** (`E4`, `#1272`).
   *
   * `depth` is this branch's own conditional's level, so a decision placed *here* is one deeper. Read
   * once and used twice: an `If…` this component would then refuse to render is the *offered name
   * with no writer* family, and a rendering it never offers is a capability that goes missing.
   */
  const mayNest = depth < MAX_CONDITIONAL_NESTING;

  return (
    /**
     * **The branch is a named region** (`E4`).
     *
     * `Then` and `Otherwise` are the only thing on screen saying which outcome a control belongs to,
     * and at two levels there are four of them — so a reader who cannot see the nesting has no way to
     * tell one `If…` from another. Naming the region is what gives them the same fact the indentation
     * gives everybody else (`#1276`: ask who could read it before and cannot now).
     */
    <Box
      role="group"
      aria-label={label}
      bg="info.tint"
      borderRadius="6px"
      p={2}
      border="1px solid"
      borderColor="info.border"
    >
      <VStack align="stretch" gap={2}>
        <HStack gap={3} align="center" wrap="wrap">
          <Text fontSize="xs" fontWeight="700" color="fg.title" minW="40px">
            {label}
          </Text>
          <BranchWireSelect expr={expr} onChange={onChange} onUnanswer={onUnanswer} />
        </HStack>
        {expr === undefined ? (
          /**
           * **`X41` says `Otherwise` is always *offered*, which is not the same as always *invented***
           * (`#1251`). A conditional with no `else` resolves to nothing, and *nothing* is not one
           * outcome — it is whatever the surface does with an unresolved value. So this states the
           * mechanism's effect where it has one and does not claim a single answer where it does not.
           */
          <Text fontSize="xs" color="fg.muted">
            No answer is given, so this expression resolves to nothing when no case matches — and what that means
            belongs to the row: a body field keeps whatever bidBrain built, a required query parameter is sent empty,
            and an optional one is left off.
          </Text>
        ) : expr.type === 'suppress' ? (
          // The outcome, not the mechanism (#1045) — and it names the second effect, which is the one
          // an operator cannot guess: suppression also strips what the driver built at this path.
          <Text fontSize="xs" color="fg.muted">
            When this branch is taken the key is not sent, and anything bidBrain would have built at this path is
            removed too.
          </Text>
        ) : (
          <>
            <ExpressionValueLine
              expression={expr}
              onChange={onChange}
              /* An unanswered branch has its own sentence above, so the empty box means one thing here. */
              placeholder={null}
              leading={null}
              pathSuggestions={getFieldSuggestions}
              /* **A branch outcome is the value that goes out**, so *what if it does not resolve* is
                 a question about it. The `Otherwise` answers *which branch*, never *what this branch
                 sends when its own path is absent* — different questions, and `#1241` already treats
                 a branch's default as a real slot by carrying it through a reshape. */
              offersDefault={true}
              /**
               * **Nothing is refused here for being a shape** any more (`E4`). What is refused is a
               * nesting deeper than the editor reads, and only above the bound — pattern 24, disabled
               * with its sentence rather than removed.
               *
               * `shared` is **not** refused: a branch may address a shared expression since `E4`, and
               * the picker below is where the surface's own answer about the library gets stated. That
               * is what makes {@link BRANCH_NESTING_BOUND}'s redirect a route rather than a sentence.
               */
              /* A branch **takes** a decision — the bound is about how deep, which is a different
                 claim about the same chip and is this slot's own to make (`E4`). */
              acceptsConditional={true}
              refuses={mayNest ? {} : { if: BRANCH_NESTING_BOUND }}
              /**
               * **`E1`/`#542`: a branch reshapes in place, exactly as the editor's own line does.**
               *
               * There is nowhere further up to hand a branch's value to, so every errand is a reshape
               * — `reshapeExpression` carries the path or the literal into the first operand or the
               * first branch. `Shared…` is the exception and always was: picking **replaces** the
               * value, so it opens a picker rather than carrying anything into one.
               */
              onOpenInEditor={(slot) => {
                if (slot === 'if' && !mayNest) return;
                if (slot === 'if' || slot === 'calculate') {
                  onChange(reshapeExpression(expr, slot === 'if' ? 'conditional' : 'computed'));
                }
              }}
              /* The chip marks the shape the branch already has, which is what the form below is —
                 the same reading as the editor's own line. What the shared slot opens wins over it,
                 inside the line. */
              openSlot={openSlotForShape(expr)}
              sharedSlot={shared}
            />

            {/* **An empty *Send this value* says what it sends** (Todd, 2026-09-29, option (a)). It is an
                answer, and on a query parameter the answer is `name=`: the box looked like *nothing*
                and sent `cid=`. Said per surface, because the header, the body and the response do
                not send it. */}
            {expr.type === 'static' && expr.value === '' && emptySentence && (
              <Text fontSize="xs" color="warning.ink" mt={1}>
                {emptySentence}
              </Text>
            )}

            {/* **The two surfaces the chip opens**, placed here so a branch draws them inside its
                own box. While the picker is open it is the only thing under the chip row — an editor
                for a value about to be replaced is two live surfaces for one decision (`#1229`). */}
            <OutsideExpressionLocations>
              <SharedExpressionSurfaces slot={shared} onChange={onChange} getFieldSuggestions={getFieldSuggestions} />
            </OutsideExpressionLocations>

            {/* **The structured shapes a branch can hold** (`X38`: only a shape with parts the value
                line cannot draw gets a form). A `static`, a `calculated` and a `reference` are entirely
                on the line above. */}
            {!shared.pickerOpen && expr.type === 'computed' && (
              <ComputedExpressionForm expr={expr} onChange={onChange} />
            )}

            {!shared.pickerOpen &&
              expr.type === 'conditional' &&
              (mayNest ? (
                <ConditionalExpressionForm expr={expr} onChange={onChange} depth={depth + 1} />
              ) : (
                <Box bg="bg.card" borderRadius="6px" p={2} border="1px solid" borderColor="border.default">
                  <Text fontSize="xs" color="fg.muted">
                    {BRANCH_TOO_DEEP_TO_SHOW}
                  </Text>
                </Box>
              ))}
          </>
        )}
      </VStack>
    </Box>
  );
};

interface ConditionalEditorProps {
  expr: ConditionalExpression;
  onChange: (expr: ConditionalExpression) => void;
  /**
   * **This conditional's own nesting level**, 1 for the value's own (`E4`, `#1272`).
   *
   * Required rather than defaulted: a default would be a claim that whoever rendered this knows it is
   * the outermost one, and the component that gets that wrong is precisely the nested one.
   */
  depth: number;
}

/*
 * `isValidBranchExpression` was deleted in `E4`. It answered *may a branch hold this shape*, and the
 * answer is now **yes, always** — a branch holds a `MacroExpression`, as `libs/dto` always said. What
 * replaced it is not a wider guard but a different question, asked of the rendering rather than of the
 * value: `depth < MAX_CONDITIONAL_NESTING`.
 *
 * It is worth a note rather than a silent removal, because a guard that admits everything reads as
 * safety and provides none — and three call sites here were `guard(x) && onChange(x)`, which would
 * have gone on silently dropping a branch value the day the guard and the model disagreed again.
 */

/**
 * One row of the condition list: path, operator, and a right-hand side whose shape follows the
 * operator (`X2`, `XS1`).
 */
interface ConditionRowProps {
  condition: MacroCondition;
  onChange: (condition: MacroCondition) => void;
  /** `null` declines the affordance — a single-condition list has nothing to remove down to. */
  onRemove: (() => void) | null;
  index: number;
}

/**
 * The operator vocabulary as the row offers it.
 *
 * **The labels are the sentence, not the model's words** — the model keeps the discriminant
 * `MacroCondition['operator']` is declared on, and the parenthesised spelling beside each label is
 * what makes a stored document readable by somebody who has only seen the screen. Carried verbatim
 * from the `operatorCollection` this replaced (`NativeSelect` for `#1242`: 35 of 35 render plain
 * text, and it needs no `Portal` wrapper to avoid `#1240`).
 *
 * `#1196` is the issue that gives this vocabulary a single home; until it lands this is one of its
 * four hand-kept copies, and that is recorded there rather than duplicated here.
 */
const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  exists: 'exists',
  notExists: 'not exists',
  eq: 'equals (eq)',
  neq: 'not equals (neq)',
  gt: 'greater than (gt)',
  gte: 'greater or equal (gte)',
  lt: 'less than (lt)',
  lte: 'less or equal (lte)',
  contains: 'contains all of',
  matches: 'matches (regex)',
  isAnyOf: 'is any of (list)',
};

/**
 * **Offered from the vocabulary, not beside it** (`#1287`). A hand-written list of options was the
 * fourth of `#1196`'s four homes and the one that had already drifted — `isOneOf` reached the DTO
 * with `#1069` and this dropdown for eleven weeks after. A `Record` keyed on the union means a new
 * member fails to compile until it has a label, and `condition-operator-vocabulary.test.ts` asserts
 * the offered set against `CONDITION_OPERATORS` in both stores.
 */
export const OPERATOR_OPTIONS: { label: string; value: ConditionOperator }[] = CONDITION_OPERATORS.map((value) => ({
  value,
  label: OPERATOR_LABELS[value],
}));

/**
 * **What the two list operators do with a list, said once, at the comparison** (`#1287`).
 *
 * It is a `?` rather than standing prose because it answers *what is this control for* — `X28b`'s
 * first row — and because a sentence on every condition row is the wallpaper `#1248` measured. It
 * sits on the **operator** select rather than on either operand because *all* versus *any* is a
 * property of the comparison, which is also why the case switch and the remove control ride there.
 *
 * The claim is genuinely new information: a value line resolving to a list is invisible until the
 * request runs, and until `#1287` the answer depended on which side held the list and how the
 * operator had typed it.
 */
const LIST_SEMANTICS_HELP =
  'Either side may resolve to a list — a path holding ["kids","family"], say. "contains all of" is ' +
  'true when the left side holds every value on the right; "is any of" is true when any value on ' +
  'the left is in the list on the right.';

/**
 * **The one errand an operand refuses is now declared rather than written here** (`X53`
 * sub-question 1, `#1284`) — `acceptsConditional={false}` on its value line, with the sentence in
 * `ExpressionValueLine`.
 *
 * The history is the reason it moved. It refused **two**, on the argument that they were *the same
 * two a branch refuses, for the same two reasons, said once* (`X50`) — true, and it is how the
 * refusal went stale: `E4` lifted both for a branch and this was justified by pointing at them
 * (`#1295`). *A refusal that derives its reason from another surface expires when that surface
 * changes, silently.* Declaring the property instead removes the pointing: a slot says what it
 * takes, and the sentence has one home.
 */

/**
 * **An operand, as a value line** — either side of a comparison (`X50`, `E2b`, `#1275`), and every
 * operand of a calculation (`X43`, `E3`, `#1271`).
 *
 * Todd, verifying `E1`: *"the compare value… right now the choices are static and a `Use path` check
 * box. It should likely be the same as the other inputs — auto selects between static, path, and the
 * ability to choose calc, extract, transform."* So the checkbox went, and with `E3` so did the
 * operand row's `Type: literal / path / fn` dropdown. Both were the **last surviving controls for a
 * decision the value line already makes** (`X24` derives the kind from what was typed; `X39` made
 * the chips the shape control), which is `F5`'s shape exactly.
 *
 * **It is one component because the third call site arrived**, which is the condition this file
 * already recorded: the branch form rendered the nested-shape line below its value and the condition
 * operand did not, so the `Calculate…` chip was inert until a rehearsal caught it. Two call sites
 * for one idea is a coincidence; three is the shared *what does this shape need below it*.
 */
const ValueOperandLine: React.FC<{
  /** Rendered above the line — a condition's *Value to test*. `null` where position names the row. */
  label: string | null;
  operand: ComputedOperand | undefined;
  onChange: (operand: ValueOperand) => void;
  /**
   * **Where each member of a list may land** (`CO8`, `2e`) — `null` declares that this slot takes one
   * value. A calculation's operand folds a list; a condition's does not, because `contains` and
   * `isAnyOf` already read a list side as members (`#1287`) and a second way to hand them one would be
   * two readings of one operand. Required and nullable, so a new caller has to answer.
   */
  onCollection: ((operand: CollectionOperand) => void) | null;
  /**
   * The remove affordance and what to call it, together — `null` declines it, which is the answer a
   * one-operand list and a two-sided comparison both give. One prop rather than two because a
   * handler with no label is an unnameable button, and the pair cannot come apart.
   */
  remove: { label: string; act: () => void } | null;
}> = ({ label, operand, onChange, onCollection, remove }) => {
  const getFieldSuggestions = useFieldSuggestions();
  /* A collection is drawn as the path it is authored as (`pod.cachedBidRefs[*].bid`), so the line,
     the type-down and the chips are the ordinary ones; `accept` reads it back. */
  const value = operand ? operandOnLine(operand) : defaultStaticExpression();
  const isCollection = operand?.type === 'collection';
  /* A slot that folds a list offers `[*]` beside every list, and the members' fields past it; one that
     does not never offers it, so the ordinary type-down cannot invite a path that resolves to nothing. */
  const pathSuggestions: FieldSuggestionsFn = onCollection
    ? async (query) => eachMemberSuggestions(query, await getFieldSuggestions(query))
    : getFieldSuggestions;
  /**
   * **Unset and empty are two answers, and the box must say which** (`#1384`, option 2, Todd
   * 2026-09-19). An operand never set is drawn with a blank literal so it can be typed into — but
   * the engine compares it as a side that does not resolve (`eq` never holds, `neq` always does),
   * where an empty literal is `''`, a value. Same empty box, two behaviours; the placeholder is what
   * separates them.
   */
  const placeholder =
    operand === undefined
      ? 'Not set — compares as missing'
      : operand.type === 'static' && operand.value === ''
        ? 'An empty value'
        : null;
  /* The same three acts a row has, at operand depth (`X30c`, `#1303`) — this held its own copy of
     the older *open the picker* behaviour, so an addressed calculation could not be opened. */
  const shared = useSharedExpressionSlot(value);
  /**
   * **The type boundary, not the policy** (`X53` sub-question 1, `#1284`).
   *
   * `ValueOperand` is four of the six members, so something has to narrow `MacroExpression` to it.
   * What this must not be is where *which shapes may appear here* is **decided** — it was, and the
   * decision was a silent drop: no control could reach it and no reader could see it. The policy is
   * `acceptsConditional` on the line below, which refuses the chip with a sentence; this is what the
   * compiler needs afterwards, and it now cannot fire from any authoring path.
   */
  const accept = (next: MacroExpression) => {
    if (next.type === 'suppress' || next.type === 'conditional') return;
    const read = onCollection ? operandFromLine(next) : next;
    if (read.type === 'collection') onCollection?.(read);
    else onChange(read);
  };
  return (
    <Box>
      {label !== null && (
        <Text fontSize="xs" color="fg.muted" mb={1}>
          {label}
        </Text>
      )}
      <HStack gap={2} align="flex-start">
        <Box flex="1" minWidth={0}>
          <ExpressionValueLine
            expression={value}
            onChange={accept}
            placeholder={placeholder}
            leading={null}
            pathSuggestions={pathSuggestions}
            /**
             * **An operand answers nothing on its own — the scope does** (`#1299`, Todd 2026-09-10).
             *
             * A comparison operand's default made an **absent** value satisfy `eq` while `exists` on
             * the same path said it was not there, and the answers an operator actually wants there
             * are `exists`, a second `any` condition, or the ladder's `Otherwise`. A calculation
             * operand's belongs at the result of the calculation.
             *
             * **Except where the operand is itself a calculation**, which is a scope: *"anyplace they
             * see the equation written out, they would think 'evaluate this expression and use this
             * if it's null/nan'"* (Todd). So a group carries one at every depth, and `#1300` is what
             * makes that answer reachable — before it, an unresolved operand became `0` and the scope
             * default never fired.
             */
            offersDefault={value.type === 'computed'}
            /* `shared` is **not** refused since `#1295`: a condition operand may address a shared
               expression, which is what makes *author the calculation twice* into *address it twice*
               and stops the two copies diverging on the first edit. */
            /* **A decision is not a value, and nothing designs one inside a calculation.** Declared
               rather than refused by hand: the sentence is the value line's, so two slots refusing
               the same thing cannot come to say it differently (`#1187`). It also replaces a guard
               that dropped a conditional **silently** — `accept` below is now the declaration's
               echo rather than the only thing enforcing it. */
            acceptsConditional={false}
            refuses={{}}
            /**
             * Reshapes in place: there is nowhere above an operand to hand a value to, which is the
             * same answer a branch gives and for the same reason. On an operand of a calculation it
             * is also how a **group** is authored — `Calculate…` makes the operand a `computed`, and
             * clearing the chip collapses it back through `reshapeExpression`, so the group's round
             * trip is the round trip every other shape already has.
             */
            onOpenInEditor={(slot) => {
              if (slot !== 'calculate') return;
              accept(reshapeExpression(value, 'computed'));
            }}
            /* The shape the operand already has. What the shared slot opens wins over it, inside the
               line — the same ordering the branch and the row both get. */
            openSlot={openSlotForShape(value)}
            sharedSlot={shared}
          />
        </Box>
        {remove && (
          <Button
            size="xs"
            variant="outline"
            borderColor="border.default"
            color="error.solid"
            aria-label={remove.label}
            onClick={remove.act}
          >
            ✕
          </Button>
        )}
      </HStack>
      {/**
       * **The order a list's members are folded in is stated where there is a list** (`CO8`) — Todd:
       * *"divide & subtract are a bit weird, order isn't necessarily guaranteed."* So it is not refused,
       * it is said: on the operand that is many, which is the one that contradicts *an operand is one
       * value*, and nowhere else.
       */}
      {isCollection && (
        <Text fontSize="xs" color="fg.muted" mt={1}>
          Each member of the list, in the list's own order. A member with no value is left out.
        </Text>
      )}
      {/* Under the line and alone, for the reason the branch's are: the value is about to be
          replaced, so an editor for it is a control whose every keystroke is about to be discarded. */}
      <OutsideExpressionLocations>
        <SharedExpressionSurfaces slot={shared} onChange={accept} getFieldSuggestions={getFieldSuggestions} />
      </OutsideExpressionLocations>
      {/**
       * **The one structured shape an operand can hold, and forgetting it made the chip inert.**
       *
       * Caught by a rehearsal rather than by a test in `E2b`: `Calculate…` reshaped the operand to
       * `computed` and nothing rendered its operand list, so the chip looked live and did nothing —
       * the *offered name with no writer* family, in the control added that slice.
       *
       * It is also where the recursion is: a group renders a form whose operands render this line.
       * `#1272` (`E4`) measures where a reader stops following that; until it does, the model goes
       * deeper than the screen stays legible.
       */}
      {!shared.pickerOpen && value.type === 'computed' && <ComputedExpressionForm expr={value} onChange={accept} />}
    </Box>
  );
};

const ConditionRow: React.FC<ConditionRowProps> = ({ condition, onChange, onRemove, index }) => {
  const getFieldSuggestions = useFieldSuggestions();
  const update = (patch: Partial<MacroCondition>) => onChange({ ...condition, ...patch });

  const { operator, left, right, values } = readConditionOperands(condition);
  const shape = conditionRhsShape(operator);
  const offersCaseControl = comparesText(operator);

  /**
   * **Writing an operand retires the spelling it replaced, on that side only.**
   *
   * `X3`'s other half: the legacy spellings are *read and never written*, so a row the operator
   * touches stops carrying two answers for one slot. Cleared per side rather than wholesale — a
   * condition whose left side is edited has said nothing about its right.
   */
  const setLeft = (operand: ValueOperand) => update({ left: operand, path: undefined });
  const setRight = (operand: ValueOperand) =>
    update({ right: operand, value: shape === 'list' ? condition.value : undefined, valuePath: undefined });

  return (
    <VStack align="stretch" gap={2}>
      <AtLocation step={{ kind: 'side', side: 'left' }}>
        <ValueOperandLine label="Value to test" operand={left} onChange={setLeft} onCollection={null} remove={null} />
      </AtLocation>
      {/**
       * **The operator reads *between* its operands** — Todd's *"value, operand, value"* model
       * (`#1259`), which he said of `Calculate` and which lands here verbatim.
       *
       * It cannot be one literal line: each operand is a value line, which is an input **and** a chip
       * row, so putting the operator beside the first one leaves it floating against a two-row
       * control with the second operand nowhere near it. On its own row between them, the row reads
       * top to bottom as *this value · compared how · with that value*, which is the sentence an
       * operator is composing.
       *
       * The case-sensitivity switch and the remove control ride here for the same reason: they are
       * about the **comparison**, not about either operand, and on the first operand's row they read
       * as belonging to it.
       */}
      <HStack gap={2} align="center" wrap="wrap">
        <Box minW="160px" flex="0 0 auto">
          {/* `NativeSelect` for `#1242`'s measurement — it renders plain text — and so it needs no
              `Portal` wrapper to avoid `#1240`'s push-the-layout defect. */}
          <NativeSelect.Root size="sm">
            <NativeSelect.Field
              aria-label={`Operator for condition ${index + 1}`}
              // The **normalised** operator, so a stored `isOneOf` opens on `is any of` rather than
              // on whatever a select does with a value none of its options carries (`#1287`).
              value={operator}
              onChange={(e) => {
                const chosen = e.currentTarget.value as ConditionOperator;
                const wasShape = conditionRhsShape(operator);
                const nowShape = conditionRhsShape(chosen);
                // Moving between a scalar and a list right-hand side changes what the value *means*,
                // so carrying it over would silently reinterpret it — `['US']` read as the string
                // `US`, or the reverse. Clearing is the honest answer, and it is the rule the old
                // row already had; what changed is that the shape is now the operator's declaration
                // (`X10`) rather than a `rhsIsList` check at the comparison.
                const keeps = wasShape === nowShape;
                update({
                  operator: chosen,
                  right: keeps ? condition.right : undefined,
                  values: keeps ? condition.values : undefined,
                  value: keeps ? condition.value : undefined,
                  valuePath: keeps ? condition.valuePath : undefined,
                });
              }}
            >
              {OPERATOR_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Box>
        <FieldHelpTooltip ariaLabel="What the comparison does with a list" content={LIST_SEMANTICS_HELP} />
        {shape === 'none' && (
          // `exists` reads no right-hand side, so the row ends here and says so rather than leaving
          // the operator looking like it lost its second operand.
          <Text fontSize="xs" color="fg.subtle">
            — nothing to compare against
          </Text>
        )}
        {offersCaseControl && (
          <Checkbox.Root
            checked={!!condition.caseSensitive}
            onCheckedChange={(d) => update({ caseSensitive: d.checked ? true : undefined })}
            size="sm"
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label>
              <Text fontSize="xs" color="fg.muted">
                Case sensitive
              </Text>
            </Checkbox.Label>
          </Checkbox.Root>
        )}
        <Box flex="0 0 auto">
          <Button
            size="xs"
            variant="ghost"
            aria-label={`Remove condition ${index + 1}`}
            onClick={() => onRemove?.()}
            disabled={!onRemove}
            color="fg.muted"
          >
            ✕
          </Button>
        </Box>
      </HStack>

      {/**
       * **The right-hand side's shape is the operator's declaration** (`X10`, applied to an operator
       * rather than a destination): `exists` declares none, `isAnyOf` declares a list, the rest
       * declare a scalar. So there is no `showRhs` flag and no `isList` check here — the control
       * follows `conditionRhsShape`, and an operator that reads no right-hand side renders none
       * rather than offering a box nobody reads (`#1017`'s family, in a condition row).
       */}
      {shape === 'list' && (
        <Box>
          <Text fontSize="xs" color="fg.muted" mb={1}>
            Values (comma-separated)
          </Text>
          {/**
           * **Either an authored list or a reference to one** (`#1280`, reported by Todd against
           * `E2b`). The model, the reader and the engine all resolve a path holding a list; until
           * this the editor could not author one, which is the `#1017` family from the other end.
           *
           * Not a value line: a value line produces **one** value, and *every slot accepts every
           * kind* for a list operand is still `XS4`'s (`#1118`/`#1130`). What this is instead is one
           * draft read two ways, by the same `readsAsPath` every other operand uses.
           */}
          <ListOrPathField
            members={values}
            path={right?.type === 'calculated' ? right.path : undefined}
            placeholder="e.g. kids, family — or a path holding a list"
            getSuggestions={getFieldSuggestions}
            onChange={(next) =>
              next.kind === 'path'
                ? update({
                    right: { type: 'calculated', path: next.path },
                    values: undefined,
                    value: undefined,
                    valuePath: undefined,
                  })
                : update({ values: next.members, right: undefined, value: undefined, valuePath: undefined })
            }
          />
        </Box>
      )}
      {shape === 'scalar' && (
        /**
         * **A legacy list under a scalar operator is shown as the text it reads as** (`#1287`).
         *
         * `readConditionOperands` stopped flattening a stored array into `'US,CA'` because the
         * *engine* must see the members. This slot's control is a value line and holds one value, so
         * the row would otherwise open **empty** over a document that has something in it — the
         * silent loss the legacy read exists to prevent, arriving at the other end of it.
         *
         * It is a render, not a store: nothing rewrites the document until the operator types, and
         * the moment they do, `setRight` clears the legacy spelling. The joined text is also what
         * `contains` now means by that list — every member — which is why this is honest rather than
         * an approximation the row could save (`flattenPredicate`'s `null` is for the cases where it
         * would not be).
         */
        <AtLocation step={{ kind: 'side', side: 'right' }}>
          <ValueOperandLine
            label="Compare with"
            operand={right ?? (values ? { type: 'static', value: values.join(', ') } : undefined)}
            onChange={setRight}
            onCollection={null}
            remove={null}
          />
        </AtLocation>
      )}
    </VStack>
  );
};

/**
 * **One predicate, edited: *Match all/any of these conditions*, the condition rows, and `+ Add
 * condition`** (`X2`, `RQ6`). Lifted out of a conditional's case (slice 5) so a round's condition is
 * written with the same control rather than a second one: a predicate is a predicate wherever it
 * decides something.
 *
 * - `scope` is what a condition here may read of values tested earlier in the same expression;
 *   `null` outside an expression, where there are none.
 * - `getFieldSuggestions` replaces the enclosing editor's path type-down for these rows; `null` keeps
 *   it. A round condition passes its own, because it reads what planning holds (`roundCondition`).
 */
export const PredicateEditor: React.FC<{
  predicate: CriteriaNode | MacroCondition | undefined;
  onChange: (predicate: CriteriaNode) => void;
  scope: TestedScope | null;
  getFieldSuggestions: FieldSuggestionsFn | null;
}> = ({ predicate, onChange, scope, getFieldSuggestions }) => {
  const list = flattenPredicate(predicate ?? null);
  const setList = (mode: ConditionListMode, conditions: MacroCondition[]) =>
    onChange(predicateFromList({ mode, conditions }));

  const body =
    list === null ? (
      /**
       * `X1`'s consequence: a view that cannot show part of the value **says so** rather than
       * dropping it. A `not`, or a group nested inside a group, is a valid predicate the flat
       * builder cannot render — and rendering an approximation the operator could then save
       * is how authored logic disappears without anything failing.
       */
      <Box bg="bg.card" borderRadius="6px" p={2} border="1px solid" borderColor="border.default">
        <Text fontSize="xs" color="fg.muted">
          This condition uses grouping or negation that this builder cannot show, so it is left untouched. Edit the
          stored expression directly to change it.
        </Text>
      </Box>
    ) : (
      <VStack gap={2} align="stretch">
        {/* The toggle appears with the second condition, because that is when it first means
            anything — a one-condition `any` and a one-condition `all` are the same predicate. */}
        {list && list.conditions.length > 1 && (
          <HStack gap={2} align="center">
            <Text fontSize="xs" color="fg.muted">
              Match
            </Text>
            {/**
             * **The labels are the sentence the row starts, not the model's words** — *"Match
             * **all** of these conditions"* is what an operator is deciding, and the model keeps
             * `all`/`any` because that is what `CriteriaNode` is discriminated on. Carried from
             * the `matchModeCollection` this replaced, which is where it was written.
             *
             * `NativeSelect` rather than Chakra's: it renders plain text, which is `#1242`'s
             * measurement (35 of 35), and it needs no `Portal` wrapper to avoid `#1240`'s
             * push-the-layout defect.
             */}
            <NativeSelect.Root size="sm" width="fit-content">
              <NativeSelect.Field
                aria-label="Match all or any of these conditions"
                value={list.mode}
                onChange={(e) => setList(e.currentTarget.value as ConditionListMode, list.conditions)}
              >
                <option value="all">all</option>
                <option value="any">any</option>
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
            <Text fontSize="xs" color="fg.muted">
              of these conditions
            </Text>
          </HStack>
        )}
        {list.conditions.map((condition, i) => (
          <AtLocation key={i} step={{ kind: 'condition', index: i }}>
            <ConditionRow
              index={i}
              condition={condition}
              onChange={(next) =>
                setList(
                  list.mode,
                  list.conditions.map((c, j) => (j === i ? next : c)),
                )
              }
              onRemove={
                list.conditions.length > 1
                  ? () =>
                      setList(
                        list.mode,
                        list.conditions.filter((_, j) => j !== i),
                      )
                  : null
              }
            />
          </AtLocation>
        ))}
        <Box>
          <Button
            size="xs"
            variant="outline"
            onClick={() => setList(list.mode, [...list.conditions, defaultMacroCondition()])}
          >
            + Add condition
          </Button>
        </Box>
      </VStack>
    );

  const scoped =
    scope === null ? body : <TestedScopeContext.Provider value={scope}>{body}</TestedScopeContext.Provider>;
  return getFieldSuggestions === null ? (
    scoped
  ) : (
    <FieldSuggestionsContext.Provider value={getFieldSuggestions}>{scoped}</FieldSuggestionsContext.Provider>
  );
};

/**
 * **One rung of the ladder: *when this holds, answer with that.***
 *
 * The `If` block, its conditions, and the `Then` that depends on them — extracted from
 * `ConditionalExpressionForm` in `E2` (`#1202`) unchanged in content, because the rung was already
 * a unit: `Then` moved inside it on 2026-08-28 precisely so *a case ladder is a stack of these
 * blocks* would be an addition rather than a re-layout of a screen an operator had learned.
 */
const CaseRungForm: React.FC<{
  rung: CaseRung;
  index: number;
  /** `null` where the ladder has one rung: there is nothing to remove down to. */
  onRemove: (() => void) | null;
  /** `null` on the first rung — nowhere above to go. Likewise `onDown` on the last. */
  onUp: (() => void) | null;
  onDown: (() => void) | null;
  onChange: (rung: CaseRung) => void;
  /** The level of the ladder this rung belongs to — passed straight to its `Then` (`E4`). */
  depth: number;
  /**
   * **First-match-wins, behind a `?` beside this rung's `If`** (#1179). `null` on every rung but the
   * first, and on the first when the ladder has one rung — one case is not an order, and a claim
   * offered where it does not apply is `#1248`'s buried mark.
   */
  orderHelp: string | null;
  /** What this case's own condition may read: the cases before it, never itself (`X55b`). */
  conditionScope: TestedScope;
  /** Whether a later slot reads the value this case tested — said on the case, so the flow is visible. */
  reused: boolean;
}> = ({ rung, index, onRemove, onUp, onDown, onChange, depth, orderHelp, conditionScope, reused }) => {
  return (
    <Box bg="warning.tint" borderRadius="6px" p={2} border="1px solid" borderColor="warning.border">
      <VStack gap={2} align="stretch">
        <HStack gap={2} align="flex-end" wrap="wrap">
          <HStack gap={1} align="center" minW="20px">
            <Text fontSize="xs" fontWeight="700" color="warning.ink">
              If
            </Text>
            {orderHelp !== null && <FieldHelpTooltip ariaLabel="About the order of cases" content={orderHelp} />}
            {/* **Where the value goes, said where it comes from** (Todd on `#1247`: *how does the user
                see the value flowing through*). The reader's read-out names this case; this names the
                reverse, so neither end of the flow is invisible from the other. */}
            {reused && (
              <Text fontSize="xs" color="warning.ink" ml={2}>
                its tested value is used below
              </Text>
            )}
          </HStack>
          <Box flex="1" />
          {/**
           * **Reordering is the feature, not the polish** (`X41`): *the order is the thing that
           * needs reviewing, so it is the thing on the page.* Arrows rather than drag, because a
           * drag handle is unusable from a keyboard and the ladders an operator authors are short —
           * and `moveRung` is the model function either affordance would call, so a later drag is
           * an addition rather than a rewrite.
           */}
          {(onUp || onDown) && (
            <HStack gap={1}>
              <Button
                size="xs"
                variant="ghost"
                aria-label={`Move case ${index + 1} earlier`}
                disabled={!onUp}
                onClick={() => onUp?.()}
              >
                ↑
              </Button>
              <Button
                size="xs"
                variant="ghost"
                aria-label={`Move case ${index + 1} later`}
                disabled={!onDown}
                onClick={() => onDown?.()}
              >
                ↓
              </Button>
            </HStack>
          )}
          {onRemove && (
            <Button size="xs" variant="ghost" aria-label={`Remove case ${index + 1}`} onClick={onRemove}>
              ✕
            </Button>
          )}
        </HStack>

        <PredicateEditor
          predicate={rung.condition}
          onChange={(condition) => onChange({ ...rung, condition })}
          scope={conditionScope}
          getFieldSuggestions={null}
        />

        {/**
         * **`Then` lives inside its own `If` block, under the conditions it depends on**
         * (2026-08-28, Todd). It renders in the un-showable case too: a predicate this builder
         * cannot draw is still a rung, and its `then` is still editable — hiding it would turn *we
         * cannot show the condition* into *we cannot show the branch*, a larger claim than the truth.
         */}
        <AtLocation step={{ kind: 'then' }}>
          <BranchExpressionForm
            label="Then"
            expr={rung.then}
            onChange={(then) => onChange({ ...rung, then })}
            /* A rung with no outcome is not a rung — the only branch that may be unanswered is the
               ladder's `Otherwise`, and declining here is what makes that a statement. */
            onUnanswer={null}
            depth={depth}
          />
        </AtLocation>
      </VStack>
    </Box>
  );
};

/**
 * **A conditional is a case ladder** (`X41`, `#1202`).
 *
 * The stored shape is a right-nested `else` chain, which the engine has always resolved as
 * first-match-wins; `flattenCaseLadder` is the projection that makes the **order** visible, because
 * expressed as nesting the second condition hides inside the first one's else branch and the thing
 * that decides the answer is the thing an operator cannot see.
 *
 * Three rules, and each is a decision rather than a rendering:
 *
 * - **First match wins, said once at the top.** Ordering semantics cannot be implicit in a ladder,
 *   because two cases overlap the moment there are three of them.
 * - **Cases are reorderable and the order is visible.** See `CaseRungForm`.
 * - **`Otherwise` is always present and always answered** — *offered*, which is not the same as
 *   *invented*. See `LadderOtherwise`.
 */
const ConditionalExpressionForm: React.FC<ConditionalEditorProps> = ({ expr, onChange, depth }) => {
  const ladder = flattenCaseLadder(expr);
  const enclosing = useTestedScope();
  /**
   * **A proposed name per case, for the value it tests** (`X55b`, `S3b`). Kept across renders so the
   * name a slot writes is the name `emit` looks for; a case that already binds one uses its own. Keyed
   * by the case's condition object and never by position — `proposalsIn` says what position cost.
   */
  // Held in state, not a ref: it is read during render, and a ref may not be (react-hooks/refs). The
  // map is a cache keyed by object identity, stable for the component's life, and never replaced.
  const [proposalStore] = React.useState(() => new WeakMap<object, string>());
  const proposals = proposalsIn(proposalStore);

  /*
   * There is no `if (!ladder)` here any more (`E4`, `#1272`). It stood for the other nesting axis — a
   * rung whose outcome is itself a condition — and blanked the **whole ladder** for it, so one rung
   * the view could not follow suppressed every rung it could. `#1248`: state a claim once, at the
   * level it is true at. The claim is true of one branch, so `BranchExpressionForm` makes it.
   */
  // Every change passes through here, so a read of a proposed name stamps its case in the same write
  // (`stampReadBindings`) — nothing is armed by the click and left for a later emit.
  const emit = (next: CaseLadder) => onChange(ladderFromRungs(stampReadBindings(next, proposals)));
  const { rungs, otherwise } = ladder;
  const scope = (position: LadderPosition) => scopeAt(ladder, position, enclosing, proposals);
  const readsHere = boundReads(ladder);

  return (
    <VStack gap={2} align="stretch">
      {rungs.map((rung, index) => (
        <AtLocation key={index} step={{ kind: 'case', index }}>
          <TestedScopeContext.Provider value={scope({ kind: 'then', index })}>
            <CaseRungForm
              conditionScope={scope({ kind: 'condition', index })}
              reused={readsHere.has(flattenPredicate(rung.condition)?.conditions[0]?.bindAs ?? '')}
              depth={depth}
              // Said once, and only where there is an order to state (#1179 moved it behind the `?`).
              orderHelp={index === 0 && rungs.length > 1 ? MAPPING_HELP.caseOrder : null}
              rung={rung}
              index={index}
              onChange={(next) => emit({ ...ladder, rungs: rungs.map((r, i) => (i === index ? next : r)) })}
              onRemove={rungs.length > 1 ? () => emit({ ...ladder, rungs: rungs.filter((_, i) => i !== index) }) : null}
              onUp={index > 0 ? () => emit(moveRung(ladder, index, index - 1)) : null}
              onDown={index < rungs.length - 1 ? () => emit(moveRung(ladder, index, index + 1)) : null}
            />
          </TestedScopeContext.Provider>
        </AtLocation>
      ))}

      <Box>
        {/**
         * **`+ Add Case`**, of Todd's three candidate labels (`+ Add Case` / `+ Add Else` /
         * `+ Add If`). *Case* is the one that does not collide: the screen already has an
         * `Otherwise` block and every rung already says `If`, and `#1045` forbids reusing a name the
         * UI gives to something else.
         *
         * A new case starts as a blank condition answering with a blank value — not a copy of the
         * rung above, which would look like a shortcut and ship a duplicate predicate the operator
         * has to notice and edit.
         */}
        <Button
          size="xs"
          variant="outline"
          onClick={() =>
            emit({
              ...ladder,
              rungs: [
                ...rungs,
                {
                  condition: predicateFromList({ mode: 'all', conditions: [defaultMacroCondition()] }),
                  then: defaultStaticExpression(),
                },
              ],
            })
          }
        >
          + Add Case
        </Button>
      </Box>

      {/**
       * **One `Otherwise`, at the bottom, outside the rungs** — the one branch with no condition, and
       * the only one that may have no answer at all (`#1251`).
       */}
      <AtLocation step={{ kind: 'otherwise' }}>
        <TestedScopeContext.Provider value={scope({ kind: 'otherwise' })}>
          <BranchExpressionForm
            label="Otherwise"
            expr={otherwise}
            onChange={(next) => emit({ ...ladder, otherwise: next })}
            onUnanswer={() => emit({ ...ladder, otherwise: undefined })}
            depth={depth}
          />
        </TestedScopeContext.Provider>
      </AtLocation>
    </VStack>
  );
};

// ---------------------------------------------------------------------------
// Main DecoratorExpressionEditor
// ---------------------------------------------------------------------------

/**
 * **What creating a library entry hands back** (`X30d`, `#1304`).
 *
 * Structural rather than the console's `SharedExpression`, because what this needs is what
 * `referenceReplacing` needs: an id to address and a version to record. A caller that returns the
 * whole entry satisfies it; one that cannot is saying it does not know what it made, and a save that
 * cannot say what it made cannot be pointed at.
 */
export interface SavedLibraryEntry {
  id: string;
  updatedAt?: string;
  createdAt?: string;
}

export interface DecoratorExpressionEditorProps {
  /**
   * The current expression value.  Defaults to a static empty expression.
   */
  expression: MacroExpression;
  /** Called whenever the expression changes. */
  onChange: (expression: MacroExpression) => void;
  /** When true the editor is shown inline; when false it is shown as a panel. */
  compact?: boolean;
  /**
   * When provided, a "Save as shared" button appears that lets the user save the
   * current expression to the shared expression library.
   * Called with `(name, description, expression)` when the user confirms.
   */
  /**
   * Promote this expression into the shared library — **required, `null` to decline**.
   *
   * As `?:` this rendered nothing on whichever surface forgot it, and the button is gated on its
   * truthiness right below, so *forgot* and *declined* were the same code. That is `#1134`
   * exactly, and this is the component the button actually lives in.
   */
  onSaveToLibrary: ((name: string, description: string, expr: MacroExpression) => Promise<SavedLibraryEntry>) | null;
  /**
   * **Who owns the control that changes this expression's shape** (`X39`, `#1229`).
   *
   * There used to be a **Type** dropdown here — `Static / Dynamic / Calculated / Conditional /
   * Suppress` — and `X39` retires it: *the five types are shapes the control renders, never types an
   * author picks.* Todd, on slice 4: *"the chip should be the picker… given the functionality exists
   * with the picker, the chips should be able to do the same."*
   *
   * So the shape control is the **capability chip row**, and this says where it is:
   *
   * - `'above'` — something already renders one for this value, and a second would be two controls
   *   for one decision. `MappingControl` is that case: the chips sit on the value line.
   * - `'own'` — nothing does, so the editor renders its own. The expression library page authoring a
   *   standalone entry, and the two legacy row editors, are that case.
   *
   * Required rather than defaulted, because *"nobody renders one"* and *"somebody above does"* look
   * identical in a diff and only one of them leaves the operator able to change the shape (`#1134`).
   */
  shapeControl: 'own' | 'above';
  /**
   * Overrides the field-path autocomplete source for this editor and all nested
   * forms. Defaults to the outbound context schema; inbound response mapping
   * passes the `response.*` catalog so expression paths suggest response fields.
   */
  getFieldSuggestions?: FieldSuggestionsFn;
  /**
   * **May a branch inside this editor address a shared expression** (`E4`, `#1272`).
   *
   * Required and nullable, the `#1134` treatment: as `?:` the picker would render nothing on
   * whichever surface forgot it, and *forgot* and *declined* would be the same code. Three answers,
   * because a boolean cannot tell a missing feature from a false claim (`X33a`) — `true` offered,
   * a {@link CapabilityRefused} refused **with its reason**, `null` a surface with no library notion
   * at all.
   *
   * It is the same value `MappingControl` holds under the same name, handed down rather than
   * re-derived: a second answer to *may this surface use the library* is a second place for it to
   * drift, and the two would disagree on exactly the surface where one of them was forgotten.
   */
  offersLibrary: true | CapabilityRefused | null;
  /**
   * **Which resolve context this editor's values resolve against** (`X18`, `XS5`), for marking a
   * shared expression that cannot work here.
   *
   * `null` is a real answer and not a gap: with no context named **no claim is made** and nothing is
   * marked, because the one output this must never produce is an expression marked unusable where it
   * resolves perfectly well (`#1002` — a value reads as proof).
   */
  resolvesAgainst: ResolveSurfaces | null;
  /**
   * **Render the same control, filled and unable to be changed** (`X30a`, `#1290`).
   *
   * Ratified by Todd 2026-09-10: *"I would expect the read-only editor to behave identically to a
   * locally authored expression — opening in-line. It's the same UI to the user, just read-only with
   * the edit control."* So there is no viewer component: a bespoke one would be a second rendering of
   * the expression model that drifts from this one invisibly, because both look right on their own
   * screen — *a shared component that bundles concepts cannot be partially adopted*.
   *
   * **The mechanism is a disabled `fieldset`, and that is the point.** Dimming with `pointerEvents:
   * none` looks disabled and is still reachable by keyboard — the same distinction as `overflow:
   * hidden` removing the scrollbar and not the scroll. A native disabled fieldset disables every
   * descendant form control for real, mouse and keyboard alike, and leaves their values in the
   * accessibility tree to be read.
   *
   * **Optional, and this is where that rule draws its line.** Required-and-nullable (`#1134`) guards
   * a prop that renders **nothing** when forgotten; forgetting this one renders the ordinary editable
   * control, which is a correct default and is what every existing call site means — the same
   * reasoning `getFieldSuggestions` carries.
   */
  readOnly?: boolean;
  /**
   * **Does this editor author the expression's own `Type`** (`FD11` §5, `4e`, `#1415`).
   *
   * `true` only where the expression is **shared** — the library page and the shared panel — because
   * that is the one surface that authors an expression without rendering it, and by `X5` every
   * consumer reads it through a reference it cannot open. On a mapping row it is `null`: the row
   * carries its own `Type` knob, which is the **override**, and rendering the declaration beside it
   * would be two controls over two layers of one concept in one panel (*two controls over one slot*).
   *
   * Required and nullable (`#1134`): forgetting it renders nothing, which is how the declaration went
   * unauthorable on the surface that most needed it.
   */
  authorsYield: true | null;
  /**
   * **What an empty value does where this expression goes** (`empty-value-context.ts`). Required and
   * nullable: `null` is an editor that does not know its destination, and says nothing.
   */
  emptyValue: EmptyValueOnSurface | null;
}

/**
 * DecoratorExpressionEditor
 *
 * Inline form for authoring {@link MacroExpression} values:
 * - **static** – fixed string literal
 * - **dynamic** (type: 'calculated') – value resolved from the request context via a
 *   dot-notation path (e.g. `session.user.ipAddress`)
 * - **calculated** (type: 'computed') – combine multiple operands (paths, literals,
 *   built-in functions) using concat or arithmetic operations
 * - **conditional** – if/then/else branching over a context condition
 */
export const DecoratorExpressionEditor: React.FC<DecoratorExpressionEditorProps> = ({
  expression,
  onChange: rawOnChange,
  onSaveToLibrary,
  shapeControl,
  getFieldSuggestions,
  offersLibrary,
  resolvesAgainst,
  readOnly = false,
  authorsYield,
  emptyValue,
}) => {
  /**
   * **Canonicalise at the editor's own boundary** (`#1213`). Four inputs inside this component
   * author a context path — the calculated path, a computed operand, and both sides of a condition
   * — and every one of them bubbles through this single callback, so the rewrite happens once here
   * rather than at each. `XS3` collapses these tabs into one view of the expression model, and a
   * per-input fix would be rewritten by that slice while this survives it.
   */
  const onChange = React.useCallback(
    (next: MacroExpression) => rawOnChange(canonicalizeExpressionPaths(next)),
    [rawOnChange],
  );

  /* This line refuses the `Shared…` chip, so nothing opens from it — see `refuses` below. */
  const ownSlot = useSharedExpressionSlot(expression);

  const [savePanelOpen, setSavePanelOpen] = React.useState(false);
  const [saveName, setSaveName] = React.useState('');
  const [saveDescription, setSaveDescription] = React.useState('');

  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  /**
   * **Promoting leaves this value addressing the library, not holding a copy of it** (`X30d`,
   * `#1304`, reported by Todd 2026-09-10: *"saving as shared expression doesn't replace local copy
   * with the reference."*).
   *
   * That is `X5` — *addressed, not copied* — failing at the one moment the copy is created: the
   * entry and the row held identical expressions, diverging on the first edit to either, with
   * nothing on either screen saying they had ever been one thing.
   *
   * **So the save has to return what it created**, which is why `onSaveToLibrary` is a promise now.
   * It was fire-and-forget, and a handler that cannot say what it made cannot be pointed at.
   *
   * **The replacement happens only on success.** A failed create that swapped the value for an
   * address would point the row at nothing and lose the expression in the same act — the worst
   * available outcome, and the one a fire-and-forget call could not have avoided.
   */
  const handleSaveConfirm = () => {
    if (!saveName.trim() || !onSaveToLibrary) return;
    setSaving(true);
    setSaveError(null);
    onSaveToLibrary(saveName.trim(), saveDescription.trim(), expression)
      .then((saved) => {
        onChange(referenceReplacing(expression, saved));
        setSaveName('');
        setSaveDescription('');
        setSavePanelOpen(false);
      })
      .catch((error: unknown) => {
        // The value is untouched, and the message says so — an operator who has just been told a
        // save failed needs to know whether their expression survived it (#1045).
        setSaveError(
          error instanceof Error
            ? `${error.message} — this expression is unchanged and still authored here.`
            : 'The expression could not be saved to the library. It is unchanged and still authored here.',
        );
      })
      .finally(() => setSaving(false));
  };

  /* Stable so a branch's picker is not remounted on every keystroke elsewhere in the editor. */
  const library = React.useMemo(() => ({ offersLibrary, resolvesAgainst }), [offersLibrary, resolvesAgainst]);

  return (
    <FieldSuggestionsContext.Provider value={getFieldSuggestions ?? getSchemaSuggestions}>
      <EmptyValueContext.Provider value={emptyValue}>
        <ExpressionLibraryAccessContext.Provider value={library}>
          {/* **The whole editor, disabled for real** (`X30a`). `chakra('fieldset')` rather than a
          `Box`, because the disabling is the element's own semantics and not a style: every
          descendant input, select and button is natively disabled by it, keyboard included, which a
          dimmed `pointerEvents: none` region is not. The reset is needed because a bare `fieldset`
          carries a browser border and padding that would draw a second frame around the panel. */}
          <ReadOnlyFieldset
            disabled={readOnly}
            border="none"
            m={0}
            p={0}
            minWidth={0}
            aria-disabled={readOnly || undefined}
          >
            <VStack gap={3} align="stretch">
              {/* **No type badge** (`X38`). It named the shape a third time — the value line's read-out
            reports it, the set chip previews it, and in `above` mode the row one level up does both
            already. It also spoke the one vocabulary `X8` records as collided (*Calculated* on
            screen being `computed` underneath), which is precisely what a read-out derived from the
            value cannot do. The collapsed list badges elsewhere keep it: there, no line is open. */}
              {onSaveToLibrary && !readOnly && (
                <HStack gap={2} align="center" wrap="wrap">
                  <Button
                    size="xs"
                    variant="outline"
                    borderColor="accent.solid"
                    color="accent.solid"
                    _hover={{ bg: 'accent.subtle' }}
                    onClick={() => setSavePanelOpen((prev) => !prev)}
                  >
                    {savePanelOpen ? 'Cancel Save' : '📌 Save as shared'}
                  </Button>
                </HStack>
              )}

              {/* **The value line, where nothing above renders one** (`X38`, and `X39`'s missing half).
            `shapeControl` says who owns the shape control, and since `X38` that is the same question
            as who owns the *line* — the chips are part of it. In `above` mode the row one level up
            has the field, the Default and the chips, so drawing them here is the duplication this
            recommendation exists to remove; in `own` mode — the library page, the pixel editor —
            nothing does, so the editor renders the line itself and the kind is still derived from
            what was typed rather than picked. */}
              {shapeControl === 'own' && (
                <ExpressionValueLine
                  expression={expression}
                  onChange={onChange}
                  placeholder={null}
                  leading={null}
                  pathSuggestions={getFieldSuggestions ?? getSchemaSuggestions}
                  /* The value's own line: this is the thing that goes on the wire, and the outermost
                 scope of any calculation it holds. Both readings give the same answer. */
                  offersDefault={true}
                  refuses={{
                    // The picker belongs to the surface that has a value to replace; replacing an
                    // expression from inside its own editor is `XS5`'s question, once one can be
                    // **addressed** rather than copied. Refused rather than wired to nothing.
                    shared: {
                      /**
                       * **It names the rule, not a place** (`X30a`, `#1290`). It used to read *"pick one
                       * from the row that uses it"*, which is a redirect — and this editor now renders on
                       * a panel **opened from that row**, where the sentence points at where the operator
                       * already is. *A refusal is the least-checked sentence in the UI*, and a circular
                       * one is the version nobody notices, because it names something real.
                       */
                      reason: 'A shared expression is addressed by the value that uses it, not from inside itself.',
                    },
                  }}
                  /**
                   * **Inside the editor the errands reshape in place** — there is nowhere further up to
                   * hand off to. The first build passed a no-op here, which is a chip that looks live and
                   * does nothing: worse than a disabled one, and exactly the *offered name with no writer*
                   * family. Caught by opening the expression library page, which is the call site with no
                   * row above it.
                   */
                  onOpenInEditor={(slot) => {
                    if (slot === 'if') onChange(reshapeExpression(expression, 'conditional'));
                    else if (slot === 'calculate') onChange(reshapeExpression(expression, 'computed'));
                  }}
                  /**
                   * **This line's chips reshape rather than open**, so the editor below is always the
                   * current shape's — which is exactly what `openSlotForShape` reads. Here it marks a chip
                   * that is *already* set, which is the honest reading: this editor is that chip's.
                   */
                  openSlot={openSlotForShape(expression)}
                  /* The value's own line takes every shape the model has — it is the thing that goes
                   on the wire, and a conditional is the commonest reason to open this editor. */
                  acceptsConditional={true}
                  /* Refused above, so nothing here opens — but the slot is still answered rather than
                   left for the compiler to notice, because *refused* and *forgotten* must not be the
                   same code (`#1134`). */
                  sharedSlot={ownSlot}
                />
              )}

              {savePanelOpen && onSaveToLibrary && (
                <Box bg="bg.canvas" borderRadius="6px" p={3} border="1px solid" borderColor="accent.subtle">
                  <Text fontSize="xs" fontWeight="600" color="accent.solid" mb={2}>
                    Save expression to library
                  </Text>
                  <VStack gap={2} align="stretch">
                    <Box>
                      <Text fontSize="xs" color="fg.muted" mb={1}>
                        Name{' '}
                        <Text as="span" color="error.solid">
                          *
                        </Text>
                      </Text>
                      <Input
                        size="sm"
                        bg="bg.card"
                        value={saveName}
                        placeholder="e.g. User IP – with default"
                        onChange={(e) => setSaveName(e.target.value)}
                        borderColor="border.default"
                        _focus={{
                          borderColor: 'accent.solid',
                          boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)',
                        }}
                      />
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="fg.muted" mb={1}>
                        Description (optional)
                      </Text>
                      <Input
                        size="sm"
                        bg="bg.card"
                        value={saveDescription}
                        placeholder="Optional note about this expression"
                        onChange={(e) => setSaveDescription(e.target.value)}
                        borderColor="border.default"
                        _focus={{
                          borderColor: 'accent.solid',
                          boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)',
                        }}
                      />
                    </Box>
                    {saveError && (
                      <Text fontSize="xs" color="error.solid">
                        {saveError}
                      </Text>
                    )}
                    <HStack justify="flex-end" gap={2}>
                      <Button
                        size="xs"
                        variant="outline"
                        borderColor="border.default"
                        onClick={() => {
                          setSavePanelOpen(false);
                          setSaveName('');
                          setSaveDescription('');
                          setSaveError(null);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="xs"
                        bg="accent.solid"
                        color="white"
                        _hover={{ bg: 'accent.ink' }}
                        disabled={!saveName.trim() || saving}
                        loading={saving}
                        onClick={handleSaveConfirm}
                      >
                        Save to Library
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              )}

              {/* **The structured shapes, and only those** (`X38`). A `static` or a `calculated`
            expression is entirely on the value line — the field, its Default, and the Extract and
            Transform chips — so there is nothing left for the editor to draw. Rendering a form for
            them is what put a second path box, a second Default, a second Transform select and a
            second regex checkbox under a row that already had all four. */}
              {expression.type === 'computed' && <ComputedExpressionForm expr={expression} onChange={onChange} />}

              {/* **Level 1 is the value's own conditional** (`E4`), and this is the only place the count
            starts — the library page and a mapping row both reach it here, which is what makes *the
            same bound in both containers* a fact rather than two numbers that agree. */}
              {expression.type === 'conditional' && (
                <ConditionalExpressionForm expr={expression} onChange={onChange} depth={1} />
              )}

              {/* **Suppress has no fields, so it renders only its outcome.**
            It used to carry the type dropdown, because every other branch rendered one inside its own
            form and a type with no form would have rendered *nothing at all* — an operator picking it
            and watching the editor vanish, with no control left to change their mind with. With the
            dropdown gone (`X39`) the shape control is the chip row, which is above this and always
            present, so the one-way door closes for a better reason. */}
              {expression.type === 'suppress' && (
                // The outcome, not the mechanism (#1045) — and it names the second effect, which is the one
                // an operator cannot guess: suppression also strips what the driver built here.
                <Text fontSize="xs" color="fg.muted">
                  This key is never sent, and anything bidBrain would have built at this path is removed too.
                </Text>
              )}

              {/* **What the expression yields, at the end of its definition** (Todd, 2026-09-23: *"I
            would expect it to be at the end of the expression definition since it's declaring the
            output of the expression."*). A control's position is a claim about its subject. */}
              {authorsYield && <ExpressionYieldControl expression={expression} onChange={onChange} />}

              {/* **The note, where nothing above owns it** (`X38`).

            It writes `expression.description` — the **same slot** `MappingControl`'s `NoteControl`
            writes, under a second word for it. Two controls over one fact, in one panel, and the
            words disagreed: *Note* on the row, *Description* here. That is `X3`'s one-concept-three-
            names defect between two live controls rather than across a store, and the fix is the
            same as everywhere else in this pass — the value's own line owns it, and a surface that
            has one above renders nothing. */}
              {shapeControl === 'own' && (
                <Box>
                  <HStack gap={2} align="center" mb={1}>
                    <KnobLabel label="Note" help={MAPPING_HELP.note} muted />
                  </HStack>
                  <Input
                    size="sm"
                    bg="bg.card"
                    value={expression.description ?? ''}
                    aria-label="Note about this expression"
                    placeholder="What this expression is for"
                    onChange={(e) => onChange({ ...expression, description: e.target.value || undefined })}
                    borderColor="border.default"
                    _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)' }}
                  />
                </Box>
              )}
            </VStack>
          </ReadOnlyFieldset>
        </ExpressionLibraryAccessContext.Provider>
      </EmptyValueContext.Provider>
    </FieldSuggestionsContext.Provider>
  );
};

/**
 * **The shared expression's `Type`** — what it yields (`FD11` §5, `4e`).
 *
 * The first option is *auto*, and it **says what it derived** where the shape makes that certain
 * (`auto (number)` over an arithmetic calculation): `FD11` §4's finding, which is the operator's
 * answer and the checker's input at once. A `suppress` produces no value, so there is nothing to
 * declare, and the control says so rather than storing a type nothing reads.
 */
const ExpressionYieldControl: React.FC<{
  expression: MacroExpression;
  onChange: (next: MacroExpression) => void;
}> = ({ expression, onChange }) => {
  const { data } = useExpressionLibrary();
  const entries = data ?? [];
  const derived = derivedYield(
    expression,
    data ? (ref) => entries.find((entry) => entry.id === ref)?.expression : null,
  );
  const refused =
    expression.type === 'suppress' ? 'This expression never sends a value, so there is no type to declare.' : null;
  return (
    <HStack gap={2} align="center">
      <KnobLabel label="Type" help={MAPPING_HELP.expressionYield} disabled={!!refused} />
      <ControlHover text={refused}>
        <NativeSelect.Root size="xs" width="auto" disabled={!!refused}>
          <NativeSelect.Field
            value={refused ? '' : (expression.asType ?? '')}
            onChange={(e) => {
              const next = e.target.value;
              onChange({ ...expression, asType: isExpressionYieldType(next) ? next : undefined });
            }}
            aria-label="Type this expression yields"
            bg="bg.card"
            color="fg.default"
            fontFamily="mono"
          >
            <option value="">{derived ? `auto (${EXPRESSION_YIELD_LABEL[derived]})` : 'auto'}</option>
            {EXPRESSION_YIELD_TYPES.map((t) => (
              <option key={t} value={t}>
                {EXPRESSION_YIELD_LABEL[t]}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </ControlHover>
    </HStack>
  );
};

/**
 * A `fieldset` with the browser's own frame taken off, so `disabled` can do the one thing only this
 * element does. See {@link DecoratorExpressionEditorProps.readOnly}.
 */
const ReadOnlyFieldset = chakra('fieldset');

// ---------------------------------------------------------------------------
// Compact inline badge + popover trigger
// ---------------------------------------------------------------------------

export interface ExpressionBadgeProps {
  expression: MacroExpression | undefined;
  onEdit: () => void;
  onClear?: () => void;
  size?: 'xs' | 'sm';
}

/**
 * A small badge that displays the current expression type and provides
 * Edit / Clear controls.  Used in table cells.
 */
export const ExpressionBadge: React.FC<ExpressionBadgeProps> = ({ expression, onEdit, onClear, size = 'xs' }) => {
  if (!expression) {
    return (
      <Button size={size} variant="outline" borderColor="border.default" onClick={onEdit}>
        + Expression
      </Button>
    );
  }

  const editButton = (
    <Button size="xs" variant="outline" borderColor="border.default" onClick={onEdit}>
      Expression
    </Button>
  );

  return (
    <HStack gap={1} align="center">
      {expression.description ? (
        <Tooltip.Root openDelay={150} closeDelay={100}>
          <Tooltip.Trigger asChild>{editButton}</Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content fontSize="xs" maxW="260px">
              {expression.description}
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      ) : (
        editButton
      )}
      {onClear && (
        <Button
          size="xs"
          variant="outline"
          borderColor="border.default"
          color="error.solid"
          onClick={onClear}
          aria-label="Clear expression"
        >
          ×
        </Button>
      )}
    </HStack>
  );
};
